// Threshold-gift reward. Auto-adds variant GIFT_VARIANT_ID to the cart when the
// qualifying subtotal (cart total minus the gift line itself) crosses
// THRESHOLD_CENTS, and removes it when the cart drops back below. The gift is
// free because the product's variant price is set to €0 in the Shopify catalog
// (not via an automatic discount — see vault decisions.md 2026-04-22 for why).
// This file handles add/remove + UI syncing of the shared free-shipping-bar
// (two thresholds: free shipping → free gift).
(() => {
  const GIFT_VARIANT_ID = 52950385983754;
  const THRESHOLD_CENTS = 50000; // keep in sync with snippets/free-shipping-bar.liquid
  const FREE_SHIPPING_FALLBACK_CENTS = 10000;
  const GIFT_PROPERTY_KEY = '_auto_gift';

  let isProcessing = false;

  const i18n = (key, fallback) =>
    (window.cartRewardI18n && window.cartRewardI18n[key]) || fallback;

  const formatEuros = (cents) => {
    const locale = document.documentElement.lang || 'de-DE';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
    }).format(cents / 100);
  };

  const fetchCart = () =>
    fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());

  const addGift = () =>
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: GIFT_VARIANT_ID,
            quantity: 1,
            properties: { [GIFT_PROPERTY_KEY]: 'true' },
          },
        ],
      }),
    });

  const removeLine = (key) =>
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 }),
    });

  const findGiftLine = (cart) =>
    cart.items.find(
      (i) =>
        i.variant_id === GIFT_VARIANT_ID &&
        i.properties &&
        i.properties[GIFT_PROPERTY_KEY] === 'true'
    );

  const triggerCartRefresh = () => {
    document.dispatchEvent(new Event('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new Event('cart:updated', { bubbles: true }));
  };

  const reconcile = async () => {
    if (isProcessing) return;
    isProcessing = true;
    try {
      const cart = await fetchCart();
      const giftLine = findGiftLine(cart);
      const qualifyingCents =
        cart.total_price - (giftLine ? giftLine.final_line_price : 0);
      const qualifies = qualifyingCents >= THRESHOLD_CENTS;

      if (qualifies && !giftLine) {
        const res = await addGift();
        if (res.ok) {
          triggerCartRefresh();
          scheduleRerender();
        } else {
          console.warn('cart-reward: addGift failed', res.status);
          renderUI(cart, giftLine, qualifyingCents);
        }
      } else if (!qualifies && giftLine) {
        const res = await removeLine(giftLine.key);
        if (res.ok) {
          triggerCartRefresh();
          scheduleRerender();
        } else {
          console.warn('cart-reward: removeLine failed', res.status);
          renderUI(cart, giftLine, qualifyingCents);
        }
      } else {
        renderUI(cart, giftLine, qualifyingCents);
      }
    } catch (err) {
      console.warn('cart-reward: reconcile failed', err);
    } finally {
      isProcessing = false;
    }
  };

  // After a cart mutation triggers cart:refresh, the drawer replaces its DOM
  // asynchronously via Section Rendering API. Re-run the display-only pass
  // once the new DOM has settled so GRATIS + subtotal override stick.
  const scheduleRerender = () => {
    setTimeout(async () => {
      try {
        const cart = await fetchCart();
        const giftLine = findGiftLine(cart);
        const qualifyingCents =
          cart.total_price - (giftLine ? giftLine.final_line_price : 0);
        renderUI(cart, giftLine, qualifyingCents);
      } catch (err) {
        console.warn('cart-reward: scheduleRerender failed', err);
      }
    }, 400);
  };

  const getThresholds = () => {
    const bar = document.querySelector('[data-cart-reward-bar]');
    if (!bar) {
      return { free: FREE_SHIPPING_FALLBACK_CENTS, gift: THRESHOLD_CENTS };
    }
    return {
      free: parseInt(bar.dataset.freeShippingCents, 10) || FREE_SHIPPING_FALLBACK_CENTS,
      gift: parseInt(bar.dataset.giftThresholdCents, 10) || THRESHOLD_CENTS,
    };
  };

  const computeBarPercent = (qualifyingCents, thresholds) => {
    if (qualifyingCents <= 0) return 0;
    if (qualifyingCents >= thresholds.gift) return 100;
    if (qualifyingCents >= thresholds.free) {
      const span = thresholds.gift - thresholds.free;
      const into = qualifyingCents - thresholds.free;
      return Math.round(80 + (into / span) * 20);
    }
    return Math.round((qualifyingCents / thresholds.free) * 80);
  };

  const renderBar = (qualifyingCents) => {
    const bars = document.querySelectorAll('[data-cart-reward-bar]');
    if (bars.length === 0) return;
    const thresholds = getThresholds();
    const percent = computeBarPercent(qualifyingCents, thresholds);

    let messageHtml;
    if (qualifyingCents >= thresholds.gift) {
      messageHtml = i18n(
        'unlocked',
        'Gratis Versand und gratis Ascent Paw 🎁'
      );
    } else if (qualifyingCents >= thresholds.free) {
      const remaining = thresholds.gift - qualifyingCents;
      messageHtml = i18n(
        'remaining_gift',
        'Noch <span class="font-bold">{amount}</span> und du bekommst ein gratis <a href="/products/ascent-paw-kratzbrett-kattzenbett-kratzpappe-braun" style="text-decoration: underline">Ascent Paw</a>'
      ).replace('{amount}', formatEuros(remaining));
    } else {
      const remaining = thresholds.free - qualifyingCents;
      messageHtml = i18n(
        'remaining_shipping',
        'Noch <span class="font-bold">{amount}</span> bis zum kostenlosen Versand'
      ).replace('{amount}', formatEuros(remaining));
    }

    bars.forEach((bar) => {
      const msg = bar.querySelector('.cart-reward__message');
      const progress = bar.querySelector('progress-bar');
      if (msg) msg.innerHTML = messageHtml;
      if (progress) progress.style.setProperty('--progress', percent + '%');
    });
  };

  // Find every DOM node representing the gift line. The drawer uses
  // `#CartDrawer-Item-{key}`, the main cart page uses `#CartItem-{key}`.
  const findGiftItemNodes = (giftLine) => {
    if (!giftLine) return [];
    const ids = [
      `CartDrawer-Item-${giftLine.key}`,
      `CartItem-${giftLine.key}`,
    ];
    return ids.map((id) => document.getElementById(id)).filter(Boolean);
  };

  const overrideGiftLinePrice = (giftLine) => {
    const freeLabel = `<span class="cart-reward__free-label font-medium">${i18n('free', 'GRATIS')}</span>`;
    findGiftItemNodes(giftLine).forEach((el) => {
      // Main cart rows have TWO .price elements (unit price + line total).
      // Drawer rows have one. Override all of them.
      el.querySelectorAll('.price').forEach((priceEl) => {
        if (priceEl.dataset.rewardOverridden === 'true') return;
        priceEl.dataset.rewardOverridden = 'true';
        priceEl.innerHTML = freeLabel;
      });
    });
  };

  const adjustSubtotalDisplay = (newCents) => {
    document.querySelectorAll('.totals__subtotal-value').forEach((el) => {
      if (!el.dataset.rewardOrigText) {
        el.dataset.rewardOrigText = el.textContent;
      }
      el.textContent = formatEuros(newCents);
    });
  };

  const clearSubtotalAdjustment = () => {
    document.querySelectorAll('.totals__subtotal-value').forEach((el) => {
      if (el.dataset.rewardOrigText) {
        el.textContent = el.dataset.rewardOrigText;
        delete el.dataset.rewardOrigText;
      }
    });
  };

  const renderUI = (cart, giftLine, qualifyingCents) => {
    renderBar(qualifyingCents);
    if (giftLine) {
      overrideGiftLinePrice(giftLine);
      adjustSubtotalDisplay(qualifyingCents);
    } else {
      clearSubtotalAdjustment();
    }
  };

  document.addEventListener('cart:refresh', reconcile);
  document.addEventListener('cart:updated', reconcile);
  document.addEventListener('DOMContentLoaded', reconcile);

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    reconcile();
  }
})();
